import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import Link from 'next/link';

import {Headline, P} from 'UI/components/layout/Text';

const COMPACT_DESCRIPTION_LINE_CLAMP = 2;

/**
 * The props for the `GuideCard` component.
 * Configures a card displaying the tour guide's information.
 */
interface ComponentProps {
    /** The guide's avatar / profile image URL. */
    avatarUrl?: string;
    /** A short description of the guide. */
    description?: string;
    /**
     * Renders a much smaller, horizontal byline variant (small avatar, no quote/role label) instead
     * of the default centered card - used as an author footer below an article's content.
     *
     * @default false
     */
    isCompact?: boolean;
    /** The guide's name. */
    name: string;
    /** Link to the guide's detail page. */
    profileHref?: string;
    /** An optional quote or description from the guide. */
    quote?: string;
    /** The label above the guide name (e.g. "Local Storyteller"). */
    roleLabel?: string;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'GuideCard'
     */
    testId?: string;
    /** Optional URL to the guide's website/profile. */
    websiteLabel?: string;
    /** Optional URL to the guide's website/profile. */
    websiteUrl?: string;
}

/**
 * A card displaying the tour guide/storyteller's avatar, name, quote, and optional link.
 * Matches the Stitch design's "Created By" / guide section with surfaceHigh background.
 *
 * @param props              The component props.
 * @param props.avatarUrl    URL for the guide's profile image.
 * @param props.description  Short description of the guide.
 * @param props.isCompact    Renders a small horizontal byline variant. Defaults to `false`.
 * @param props.name         The guide's display name.
 * @param props.profileHref  Link to the guide's detail page.
 * @param props.quote        Optional guide quote.
 * @param props.roleLabel    Optional role label.
 * @param props.testId       An optional test ID used for automation selectors. Defaults to `'GuideCard'`.
 * @param props.websiteLabel Optional label for the guide's website link.
 * @param props.websiteUrl   Optional URL to the guide's website/profile.
 * @returns A JSX element rendering the GuideCard.
 *
 * @example
 * ```tsx
 * <GuideCard
 *     avatarUrl="/media/lukas.jpg"
 *     description="Augsburg-Kenner seit 15 Jahren"
 *     name="Lukas M."
 *     profileHref="/guides/lukas-m"
 *     quote="I've spent 15 years uncovering Augsburg's secrets..."
 *     roleLabel="Local Storyteller"
 *     websiteLabel="View Profile"
 *     websiteUrl="https://example.com"
 * />
 * ```
 */
const GuideCard = ({
    avatarUrl,
    description,
    isCompact = false,
    name,
    profileHref,
    quote,
    roleLabel,
    testId = 'GuideCard',
    websiteLabel,
    websiteUrl
}: ComponentProps) => (
    <Card $isCompact={isCompact} data-cy={testId}>
        {avatarUrl && (
            <Avatar $isCompact={isCompact} alt={name} data-cy={`${testId}-avatar`} src={avatarUrl} />
        )}
        <Content>
            {!isCompact && roleLabel && <RoleLabel $size="labelMedium" as="span">{roleLabel}</RoleLabel>}
            <GuideName $isCompact={isCompact} $size="headline" as="h3">{name}</GuideName>
            {description && <Description $isCompact={isCompact} $size="body">{description}</Description>}
            {!isCompact && quote && <Quote $size="body">{quote}</Quote>}
            {profileHref && (
                <ProfilePageLink data-cy={`${testId}-profile-link`} href={profileHref}>
                    Profil ansehen
                </ProfilePageLink>
            )}
            {websiteUrl && (
                <WebsiteLink data-cy={`${testId}-link`} href={websiteUrl} rel="noopener noreferrer" target="_blank">
                    {websiteLabel}
                </WebsiteLink>
            )}
        </Content>
    </Card>
);

GuideCard.displayName = 'GuideCard';

export {GuideCard};

// ── Styled ──────────────────────────────────────────────────

interface CompactProps {
    $isCompact: boolean;
}

const Card = styled.section<CompactProps>`
    align-items: center;
    background: ${({theme}) => theme.colors.surfaceHigh};
    border-radius: ${({$isCompact}) => ($isCompact ? spacing(3) : spacing(5))};
    display: flex;
    flex-direction: ${({$isCompact}) => ($isCompact ? 'row' : 'column')};
    gap: ${({$isCompact}) => ($isCompact ? spacing(3) : spacing(4))};
    padding: ${({$isCompact}) => ($isCompact ? spacing(3) : spacing(6))};
    text-align: ${({$isCompact}) => ($isCompact ? 'left' : 'center')};
`;

const Avatar = styled.img<CompactProps>`
    border: ${({$isCompact}) => ($isCompact ? '2px' : '4px')} solid ${({theme}) => theme.colors.white};
    border-radius: 50%;
    box-shadow: ${({theme}) => theme.boxShadows.cardShadow};
    flex-shrink: 0;
    height: ${({$isCompact}) => ($isCompact ? '4.4rem' : '8rem')};
    object-fit: cover;
    width: ${({$isCompact}) => ($isCompact ? '4.4rem' : '8rem')};
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
    min-width: 0;
`;

const RoleLabel = styled(P)`
    color: ${({theme}) => theme.colors.accentDefault};
`;

const GuideName = styled(Headline)<CompactProps>`
    color: ${({theme}) => theme.colors.primaryDefault};
    font-size: ${({$isCompact}) => ($isCompact ? '1.5rem' : '2rem')};
`;

const Description = styled(P)<CompactProps>`
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({$isCompact}) => ($isCompact ? COMPACT_DESCRIPTION_LINE_CLAMP : 'unset')};
    color: ${({theme}) => theme.colors.inkSecondary};
    display: ${({$isCompact}) => ($isCompact ? '-webkit-box' : 'block')};
    line-height: 1.5;
    overflow: ${({$isCompact}) => ($isCompact ? 'hidden' : 'visible')};
`;

const Quote = styled(P)`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-style: italic;
    line-height: 1.5;
`;

const ProfilePageLink = styled(Link)`
    border-bottom: 2px solid ${({theme}) => theme.colors.primaryDefault}33;
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 700;
    text-decoration: none;
    transition: border-color 0.2s;

    &:hover {
        border-bottom-color: ${({theme}) => theme.colors.primaryDefault};
    }
`;

const WebsiteLink = styled.a`
    border-bottom: 2px solid ${({theme}) => theme.colors.primaryDefault}33;
    color: ${({theme}) => theme.colors.primaryDefault};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.4rem;
    font-weight: 700;
    text-decoration: none;
    transition: border-color 0.2s;

    &:hover {
        border-bottom-color: ${({theme}) => theme.colors.primaryDefault};
    }
`;